import { useState } from "react";
import {
  DineInLineStatus, IDineInBill, IDineInBillLine, DINEIN_MAX_QUANTITY, LINE_STATUS_META, normalizeLineStatus,
} from "../models/IDineIn";
import {
  deleteDineInOrderLine, dineInErrorCode, dineInErrorDescription, updateDineInOrderLine, updateDineInOrderLineStatus,
} from "../services/dineInService";

function money(n: number, symbol?: string): string {
  return `${n.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${symbol ? ` ${symbol}` : ""}`;
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
}

const LINE_ERROR: Record<string, string> = {
  OrderLineNotFound: "Bu satır bulunamadı — başka biri silmiş olabilir. Hesap yenilendi.",
  SessionAlreadyPaid: "Bu hesabın ödemesi zaten alınmış.",
  SessionNotOpen: "Masa boşaltılmış. Yeniden açmanız gerekiyor.",
  RestourantAccessDenied: "Bu restoranda işlem yapma yetkiniz yok.",
  OrderLinesNotInSameSession: "Seçilen ürünler aynı hesapta değil. Hesap yenilendi.",
};

/** Durum isteğinde sunucunun reddettiği ama hesabın bayatladığını gösteren kodlar — hesap tazelenir. */
const REFRESH_CODES = ["OrderLineNotFound", "SessionAlreadyPaid", "SessionNotOpen", "OrderLineStatusTransitionNotAllowed", "OrderLinesNotInSameSession"];

interface Props {
  bill: IDineInBill;
  onBillChanged: (bill: IDineInBill) => void;
  /** Satır bulunamadı gibi durumlarda hesabı baştan çekmek için */
  onNeedsRefresh: () => void;
}

export default function DineInBillPanel({ bill, onBillChanged, onNeedsRefresh }: Props) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editQty, setEditQty] = useState(1);
  const [editNote, setEditNote] = useState("");
  const [busyLineId, setBusyLineId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [error, setError] = useState("");

  const symbol = bill.session.currencyDisplayText;
  const readOnly = bill.isReadOnly;

  const startEdit = (line: IDineInBillLine) => {
    setEditingId(line.lineId);
    setEditQty(line.quantity);
    // Not "yama değil değiştirme": mevcut notu geri göndermezsek silinir.
    setEditNote(line.foodOrderNote ?? "");
    setError("");
  };

  const handleError = (err: any) => {
    const code = dineInErrorCode(err);
    setError(LINE_ERROR[code ?? ""] ?? dineInErrorDescription(err) ?? "İşlem tamamlanamadı.");
    if (code && REFRESH_CODES.includes(code)) onNeedsRefresh();
  };

  // ── Durum (Hazırlanıyor → Çıktı → Teslim edildi) ──
  const [statusBusy, setStatusBusy] = useState<string | null>(null);

  const lineStatus = (l: IDineInBillLine) => normalizeLineStatus(l.status);
  const preparingIds = bill.lines.filter((l) => lineStatus(l) === "Preparing").map((l) => l.lineId);
  const readyIds = bill.lines.filter((l) => lineStatus(l) === "Ready").map((l) => l.lineId);
  const notServedIds = [...preparingIds, ...readyIds];
  const servedCount = bill.lines.length - notServedIds.length;

  /** busyKey: tek satırda satır id'si, toplu işlemde "all-<durum>" — çift dokunmayı engeller. */
  const advance = (lineIds: string[], status: DineInLineStatus, busyKey: string) => {
    if (!lineIds.length || statusBusy) return;
    setStatusBusy(busyKey);
    setError("");
    // Sunucu sınırı: bir istekte en çok 100 satır
    updateDineInOrderLineStatus(lineIds.slice(0, 100), status)
      .then((res) => onBillChanged(res.data))
      .catch(handleError)
      .finally(() => setStatusBusy(null));
  };

  const saveEdit = (line: IDineInBillLine) => {
    setBusyLineId(line.lineId);
    setError("");
    updateDineInOrderLine(line.lineId, editQty, editNote.trim() || undefined)
      .then((res) => { onBillChanged(res.data); setEditingId(null); })
      .catch(handleError)
      .finally(() => setBusyLineId(null));
  };

  const removeLine = (line: IDineInBillLine) => {
    setBusyLineId(line.lineId);
    setError("");
    deleteDineInOrderLine(line.lineId)
      .then((res) => { onBillChanged(res.data); setConfirmDeleteId(null); })
      .catch(handleError)
      .finally(() => setBusyLineId(null));
  };

  return (
    <div className="dinein-bill">
      {error && (
        <div className="dinein-alert dinein-alert--error">
          <i className="bi bi-exclamation-circle-fill"></i>
          <span>{error}</span>
          <button type="button" onClick={() => setError("")}><i className="bi bi-x-lg"></i></button>
        </div>
      )}

      {bill.lines.length === 0 ? (
        <div className="dinein-empty-lines">
          <i className="bi bi-basket"></i>
          <div>Henüz ürün eklenmedi.</div>
        </div>
      ) : (
        <>
        {/* Durum özeti + toplu işlemler */}
        <div className="dinein-status-bar">
          <div className="dinein-status-bar__counts">
            {preparingIds.length > 0 && (
              <span className={`dinein-status ${LINE_STATUS_META.Preparing.cls}`}>
                <i className={`bi ${LINE_STATUS_META.Preparing.icon}`}></i>{preparingIds.length} hazırlanıyor
              </span>
            )}
            {readyIds.length > 0 && (
              <span className={`dinein-status ${LINE_STATUS_META.Ready.cls}`}>
                <i className={`bi ${LINE_STATUS_META.Ready.icon}`}></i>{readyIds.length} çıktı
              </span>
            )}
            {servedCount > 0 && (
              <span className={`dinein-status ${LINE_STATUS_META.Served.cls}`}>
                <i className={`bi ${LINE_STATUS_META.Served.icon}`}></i>{servedCount} teslim
              </span>
            )}
          </div>
          {!readOnly && notServedIds.length > 0 && (
            <div className="dinein-status-bar__actions">
              {preparingIds.length > 0 && (
                <button type="button" className="dinein-btn dinein-btn--ghost dinein-btn--sm" disabled={!!statusBusy}
                  onClick={() => advance(preparingIds, "Ready", "all-Ready")} title="Hazırlanan tüm ürünleri 'Çıktı' yap">
                  {statusBusy === "all-Ready" ? <span className="spinner-border spinner-border-sm" /> : <i className="bi bi-bell me-1"></i>}
                  Hepsi çıktı
                </button>
              )}
              <button type="button" className="dinein-btn dinein-btn--success dinein-btn--sm" disabled={!!statusBusy}
                onClick={() => advance(notServedIds, "Served", "all-Served")} title="Masadaki her şey geldi">
                {statusBusy === "all-Served" ? <span className="spinner-border spinner-border-sm" /> : <i className="bi bi-check2-all me-1"></i>}
                Hepsi teslim edildi
              </button>
            </div>
          )}
        </div>

        <div className="dinein-lines">
          {bill.lines.map((line) => {
            const busy = busyLineId === line.lineId;
            const isEditing = editingId === line.lineId;
            const hasDiscount = line.discountedUnitPrice < line.unitPrice;
            const status = lineStatus(line);
            const meta = LINE_STATUS_META[status];
            const statusTime = status === "Served" ? line.servedAtUtc : status === "Ready" ? line.readyAtUtc : undefined;
            const lineStatusBusy = statusBusy === line.lineId;

            return (
              <div key={line.lineId} className={`dinein-line dinein-line--${status.toLowerCase()}`}>
                <div className="dinein-line__main">
                  <div className="dinein-line__top">
                    <span className="dinein-line__qty">{line.quantity}×</span>
                    <span className="dinein-line__name">{line.foodNameTr}</span>
                    <span className="dinein-line__total">{money(line.lineTotal, symbol)}</span>
                  </div>

                  <div className="dinein-line__meta">
                    {hasDiscount ? (
                      <>
                        <s>{money(line.unitPrice, symbol)}</s>{" "}
                        <strong style={{ color: "#16a34a" }}>{money(line.discountedUnitPrice, symbol)}</strong>
                        <span className="dinein-badge dinein-badge--discount">Kampanya</span>
                      </>
                    ) : (
                      <>Birim {money(line.unitPrice, symbol)}</>
                    )}
                    {line.addedByUserName && <> · {line.addedByUserName}</>}
                    <> · {formatTime(line.addedAtUtc)}</>
                    {line.updatedAtUtc && <> · <em>düzenlendi</em></>}
                  </div>

                  {line.upgrades.length > 0 && (
                    <div className="dinein-line__upgrades">
                      {line.upgrades.map((u) => (
                        <span key={u.id} className="dinein-badge">
                          + {u.title} ×{u.quantity} ({money(u.lineAmount, symbol)})
                        </span>
                      ))}
                    </div>
                  )}

                  {line.foodOrderNote && !isEditing && (
                    <div className="dinein-line__note"><i className="bi bi-sticky me-1"></i>{line.foodOrderNote}</div>
                  )}

                  {/* Durum + sonraki adım */}
                  <div className="dinein-line__status">
                    <span className={`dinein-status ${meta.cls}`}
                      title={line.statusChangedByUserName ? `Son değiştiren: ${line.statusChangedByUserName}` : undefined}>
                      <i className={`bi ${meta.icon}`}></i>{meta.label}
                      {statusTime && <span className="dinein-status__time">{formatTime(statusTime)}</span>}
                    </span>
                    {line.statusChangedByUserName && status !== "Preparing" && (
                      <span className="dinein-small dinein-muted">{line.statusChangedByUserName}</span>
                    )}
                    {!readOnly && !isEditing && status !== "Served" && (
                      <div className="dinein-line__status-actions">
                        {status === "Preparing" && (
                          <button type="button" className="dinein-status-btn dinein-status-btn--ready" disabled={!!statusBusy}
                            onClick={() => advance([line.lineId], "Ready", line.lineId)} title="Mutfaktan çıktı">
                            <i className="bi bi-bell"></i>Çıktı
                          </button>
                        )}
                        <button type="button" className="dinein-status-btn dinein-status-btn--served" disabled={!!statusBusy}
                          onClick={() => advance([line.lineId], "Served", line.lineId)} title="Masaya teslim edildi">
                          {lineStatusBusy ? <span className="spinner-border spinner-border-sm" style={{ width: 11, height: 11 }} /> : <i className="bi bi-check2"></i>}
                          Teslim
                        </button>
                      </div>
                    )}
                  </div>

                  {isEditing && (
                    <div className="dinein-line__edit">
                      <div className="dinein-stepper">
                        <button type="button" disabled={busy || editQty <= 1} onClick={() => setEditQty((q) => Math.max(1, q - 1))}>
                          <i className="bi bi-dash-lg"></i>
                        </button>
                        <input type="number" min={1} max={DINEIN_MAX_QUANTITY} value={editQty}
                          onChange={(e) => setEditQty(Math.min(DINEIN_MAX_QUANTITY, Math.max(1, Number(e.target.value) || 1)))} />
                        <button type="button" disabled={busy || editQty >= DINEIN_MAX_QUANTITY} onClick={() => setEditQty((q) => Math.min(DINEIN_MAX_QUANTITY, q + 1))}>
                          <i className="bi bi-plus-lg"></i>
                        </button>
                      </div>
                      <input className="dinein-note-input" value={editNote} maxLength={500}
                        placeholder="Not (boş bırakılırsa silinir)"
                        onChange={(e) => setEditNote(e.target.value)} />
                      <div className="dinein-line__edit-actions">
                        <button type="button" className="dinein-btn dinein-btn--ghost" disabled={busy} onClick={() => setEditingId(null)}>
                          Vazgeç
                        </button>
                        <button type="button" className="dinein-btn dinein-btn--primary" disabled={busy} onClick={() => saveEdit(line)}>
                          {busy ? <span className="spinner-border spinner-border-sm" /> : <><i className="bi bi-check-lg me-1"></i>Kaydet</>}
                        </button>
                      </div>
                      <div className="dinein-hint">Menü ekleri düzenlenemez — yanlışsa satırı silip yeniden ekleyin.</div>
                      {status !== "Preparing" && (
                        <div className="dinein-hint" style={{ color: "#b45309" }}>
                          <i className="bi bi-exclamation-triangle me-1"></i>
                          Bu ürün {status === "Ready" ? "mutfaktan çıktı" : "masaya teslim edildi"}. Bir tane daha istenirse adeti artırmak yerine
                          <b> yeni ürün ekleyin</b> — yoksa mutfağa yeni sipariş düşmez.
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {!readOnly && !isEditing && (
                  <div className="dinein-line__actions">
                    {confirmDeleteId === line.lineId ? (
                      <>
                        <button type="button" className="dinein-icon-btn dinein-icon-btn--danger" disabled={busy} onClick={() => removeLine(line)} title="Sil">
                          {busy ? <span className="spinner-border spinner-border-sm" style={{ width: 12, height: 12 }} /> : <i className="bi bi-check-lg"></i>}
                        </button>
                        <button type="button" className="dinein-icon-btn" disabled={busy} onClick={() => setConfirmDeleteId(null)} title="Vazgeç">
                          <i className="bi bi-x-lg"></i>
                        </button>
                      </>
                    ) : (
                      <>
                        <button type="button" className="dinein-icon-btn" onClick={() => startEdit(line)} title="Düzenle">
                          <i className="bi bi-pencil"></i>
                        </button>
                        <button type="button" className="dinein-icon-btn dinein-icon-btn--danger" onClick={() => setConfirmDeleteId(line.lineId)} title="Sil">
                          <i className="bi bi-trash3"></i>
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        </>
      )}

      {/* Toplamlar */}
      <div className="dinein-totals">
        <div className="dinein-totals__row">
          <span>Ara toplam</span>
          <span>{money(bill.subTotalAmount, symbol)}</span>
        </div>
        {bill.totalDiscountAmount > 0 && (
          <div className="dinein-totals__row dinein-totals__row--discount">
            <span>Kampanya indirimi</span>
            <span>− {money(bill.totalDiscountAmount, symbol)}</span>
          </div>
        )}
        <div className="dinein-totals__row dinein-totals__row--grand">
          <span>Toplam</span>
          <span>{money(bill.totalAmount, symbol)}</span>
        </div>
      </div>
    </div>
  );
}
